import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import './LesLoges.css';

interface Loge {
  nom: string;
  orient: string;
  date: string;
  jour: string;
  image: string;
  presentation: string;
  adresseTemple: string;
}

interface Membre {
  nom: string;
  prenom: string;
  fonction: string;
  grades: string;
  statut: string;
}

function LesLoges() {
  const { id } = useParams(); // id sera le nom de la loge
  const navigate = useNavigate();
  const [loge, setLoge] = useState<Loge | null>(null);
  const [membres, setMembres] = useState<Membre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('nom');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const fetchLogeAndMembres = async () => {
      if (!id) {
        setError('Identifiant de la loge manquant');
        setLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const logeDoc = await getDoc(doc(db, 'Loges', id));
        
        if (logeDoc.exists()) {
          setLoge({
            nom: id, // Le nom est l'ID du document
            orient: logeDoc.data().orient || '',
            date: logeDoc.data().date || '',
            jour: logeDoc.data().jour || '',
            image: logeDoc.data().image || '',
            presentation: logeDoc.data().presentation || '',
            adresseTemple: logeDoc.data().adresseTemple || '',
          });

          // Récupérer les membres de la loge
          const membresQuery = query(
            collection(db, 'Membres'),
            where('loge', '==', id),
            where('statut', '==', 'Valide')
          );

          const membresSnapshot = await getDocs(membresQuery);
          const membresData = membresSnapshot.docs.map(doc => ({
            nom: doc.data().nom || '',
            prenom: doc.data().prenom || '',
            fonction: doc.data().fonction || '',
            grades: doc.data().grades || '',
            statut: doc.data().statut || ''
          }));

          setMembres(membresData);
        } else {
          setError('Loge non trouvée');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération:', err);
        setError('Erreur lors de la récupération des données');
      } finally {
        setLoading(false);
      }
    };

    fetchLogeAndMembres();
  }, [id]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleModifier = () => {
    navigate(`/mapage/modifier-loge/${id}`);
  };

  const sortedMembres = [...membres].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    const valueA = a[sortColumn as keyof typeof a]?.toLowerCase() || '';
    const valueB = b[sortColumn as keyof typeof b]?.toLowerCase() || '';
    return valueA.localeCompare(valueB) * direction;
  });

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!loge) return <div>Loge non trouvée</div>;

  return (
    <div className="les-loges-container">
      <div className="modify-button-container">
        <button onClick={handleModifier} className="modify-button">
          Modifier
        </button>
      </div>

      <div className="loge-header">
        {loge.image ? (
          <img src={loge.image} alt={loge.nom} className="loge-logo" />
        ) : (
          <div className="no-photo">Aucune photo</div>
        )}
        <h1>{loge.nom}</h1>
      </div>

      <div className="loge-info-grid">
        <div className="info-section">
          <h2>Informations</h2>
          <div className="info-card">
            <div className="info-row">
              <label>Orient:</label>
              <span>{loge.orient}</span>
            </div>
            <div className="info-row">
              <label>Tenue:</label>
              <span>{loge.date} {loge.jour}</span>
            </div>
            <div className="info-row">
              <label>Temple:</label>
              <span>{loge.adresseTemple}</span>
            </div>
          </div>
        </div>

        <div className="presentation-section">
          <h2>Présentation</h2>
          <div className="presentation-card">
            <p>{loge.presentation}</p>
          </div>
        </div>

        <div className="membres-section">
          <h2>Membres</h2>
          <div className="membres-card">
            {membres.length > 0 ? (
              <table className="membres-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('nom')} className="sortable">
                      Nom {sortColumn === 'nom' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort('prenom')} className="sortable">
                      Prénom {sortColumn === 'prenom' && (sortDirection === 'asc' ? '▲' : '▼')}
                    </th>
                    <th>Grade</th>
                    <th>Fonction</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMembres.map((membre, index) => (
                    <tr key={index}>
                      <td>{membre.nom}</td>
                      <td>{membre.prenom}</td>
                      <td>{membre.grades}</td>
                      <td>{membre.fonction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>Aucun membre listé</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LesLoges;